import React, { useCallback, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

import { PageContainer } from 'components/layout/PageContainer';
import { cn } from 'lib/utils';
import { getThemeForPath, syncBodyTheme } from 'theme/routeThemes';
import { useEventTracking } from 'hooks/useEventTracking';
import { FlowThemeTransitionProvider } from 'components/QuestionFlow/themes/FlowThemeTransition';

import { RouteBackground } from './RouteBackground';

type ThemedAppProps = {
  children: React.ReactNode;
};

export const ThemedApp: React.FC<ThemedAppProps> = ({ children }) => {
  const location = useLocation();
  const theme = getThemeForPath(location.pathname);
  const isFullWidth = theme.isFullWidth?.(location.pathname) ?? false;
  const { track } = useEventTracking();
  const coveredLocationKey = useRef<string | null>(null);
  // Remembers the last entry a module scene covered, so leaving it never uncovers unseen artwork.
  const handleCoverageChange = useCallback((locationKey: string, covered: boolean) => {
    if (covered) {
      coveredLocationKey.current = locationKey;
    } else if (coveredLocationKey.current === locationKey) {
      coveredLocationKey.current = null;
    }
  }, []);

  useEffect(() => {
    syncBodyTheme(theme.bodyClass);
    return () => {
      if (typeof document !== 'undefined' && theme.bodyClass) {
        document.body.classList.remove(theme.bodyClass);
      }
    };
  }, [theme.bodyClass]);

  useEffect(() => {
    track('page_view');
  }, [location.pathname, track]);

  useEffect(() => {
    if (typeof document === 'undefined') {
      return;
    }

    const rootElement = document.getElementById('root');
    if (!rootElement) {
      return;
    }

    let animationFrameId: number | null = null;

    const scheduleScrollbarUpdate = () => {
      if (animationFrameId !== null) {
        return;
      }

      animationFrameId = window.requestAnimationFrame(() => {
        animationFrameId = null;
        const vertical = rootElement.offsetWidth - rootElement.clientWidth;
        const horizontal = rootElement.offsetHeight - rootElement.clientHeight;

        const nextWidth = `${Math.max(vertical, 0)}px`;
        const nextHeight = `${Math.max(horizontal, 0)}px`;

        if (rootElement.style.getPropertyValue('--app-scrollbar-width') !== nextWidth) {
          rootElement.style.setProperty('--app-scrollbar-width', nextWidth);
        }
        if (rootElement.style.getPropertyValue('--app-scrollbar-height') !== nextHeight) {
          rootElement.style.setProperty('--app-scrollbar-height', nextHeight);
        }
      });
    };

    scheduleScrollbarUpdate();

    window.addEventListener('resize', scheduleScrollbarUpdate);

    let resizeObserver: ResizeObserver | undefined;

    if (typeof ResizeObserver !== 'undefined') {
      resizeObserver = new ResizeObserver(() => scheduleScrollbarUpdate());
      resizeObserver.observe(rootElement);
    }

    return () => {
      window.removeEventListener('resize', scheduleScrollbarUpdate);
      resizeObserver?.disconnect();
      if (animationFrameId !== null) {
        window.cancelAnimationFrame(animationFrameId);
      }
    };
  }, []);

  useEffect(() => {
    if (typeof document === 'undefined') {
      return;
    }

    const rootElement = document.getElementById('root');
    if (!rootElement) {
      return;
    }

    rootElement.scrollTop = 0;
    rootElement.scrollLeft = 0;
  }, [location.pathname]);

  return (
    <div className={cn('relative flex min-h-screen w-full flex-col text-foreground transition-colors', theme.wrapperClass)}>
      <RouteBackground theme={theme} locationKey={location.key} coveredLocationKey={coveredLocationKey} />
      <PageContainer
        className="relative z-10 flex flex-1 flex-col pb-4"
        maxWidth={isFullWidth ? 'none' : 'screen-xl'}
        withHorizontalPadding={!isFullWidth}
        data-full-width={isFullWidth ? 'true' : undefined}
      >
        <FlowThemeTransitionProvider onCoverageChange={handleCoverageChange}>
          {children}
        </FlowThemeTransitionProvider>
      </PageContainer>
    </div>
  );
};
